import axios from 'axios';
import { isAndroid } from '../platform/NativeBridge';
import {
  addNativeVoiceListener,
  getNativeVoicePlugin,
  isNativeVoicePluginAvailable,
} from './NativeVoicePlugin';
import { recordVoiceDiagnostic } from './voiceDiagnostics';

// Uma indisponibilidade do provedor não pode disparar dezenas de novas
// tentativas pelas várias telas/filas de fala. O bloqueio vale apenas no
// navegador atual e expira automaticamente quando o servidor permitir.
let ttsUnavailableUntil = 0;
const ATHENA_EDGE_PROVIDER = 'edge';
const CONTROLLED_RETRY_DELAY_MS = 450;

// Diagnóstico curto e sem conteúdo do usuário. A Home expõe esse buffer para
// suporte; nunca registramos transcrições ou texto sintetizado.
export function appendAthenaVoiceDiagnostic(event, details = {}) {
  if (typeof window === 'undefined') return;
  const entry = recordVoiceDiagnostic(event, {
    provider: 'edge_francisca_neural',
    native: isAndroid,
    attempt: details?.attempt,
    status: details?.status,
    apiStatus: details?.status,
    errorCode: details?.reason,
  });
  const previous = Array.isArray(window.__ATHENA_VOICE_DEBUG__) ? window.__ATHENA_VOICE_DEBUG__ : [];
  window.__ATHENA_VOICE_DEBUG__ = [...previous.slice(-39), entry];
}

const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

// The Worker can expose another provider when Edge is unavailable. Athena's
// identity is not interchangeable: a non-Francisca voice becomes text mode,
// never an automatic system/Google/Chrome fallback.
function isOfficialAthenaVoice(provider) {
  return provider === ATHENA_EDGE_PROVIDER;
}

/**
 * Obtém a voz oficial da Athena. Esta camada deliberadamente não usa
 * speechSynthesis: a identidade vocal não pode variar conforme o navegador.
 */
export async function requestAthenaTts({ api, token, text, signal, onDebug }) {
  const startedAt = performance.now();
  const debug = onDebug || appendAthenaVoiceDiagnostic;
  if (Date.now() < ttsUnavailableUntil) {
    debug('tts_backoff_active', { provider: 'remote_edge', retry_in_ms: ttsUnavailableUntil - Date.now() });
    return null;
  }

  for (let attempt = 1; attempt <= 2; attempt += 1) {
    try {
      debug('tts_attempt', { attempt, provider: 'edge_francisca_neural' });
      const response = await axios.post(`${api}/voice/tts`, { text, use_edge: true }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
        timeout: 30000,
        signal,
      });
      const provider = response.data?.provider || null;
      if (response.data?.audio && isOfficialAthenaVoice(provider)) {
        debug('tts_success', {
          attempt,
          provider: 'edge_francisca_neural',
          time_to_audio_ms: Math.round(performance.now() - startedAt),
          native_fallback_attempted: false,
          cached: !!response.data?.cached,
        });
        return { audio: response.data.audio, attempts: attempt, provider };
      }

      // The server can still return an older generic fallback. It is not
      // played by the client, so the person always hears Francisca or sees
      // text-only feedback.
      debug('tts_identity_unavailable', { attempt, provider: provider || 'unknown' });
      return null;
    } catch (error) {
      if (signal?.aborted) return null;
      const status = error?.response?.status;
      const retrySeconds = Number(error?.response?.data?.retry_after_seconds);
      const retryable = !status || status >= 500;
      debug('tts_fail', { attempt, provider: 'edge_francisca_neural', status: status || null, reason: error?.code || error?.message || 'request_failed' });
      if (attempt === 1 && retryable) {
        await wait(CONTROLLED_RETRY_DELAY_MS);
        if (!signal?.aborted) continue;
      }
      if (status === 429 || status === 503) {
        const waitMs = Math.max(5000, Math.min((Number.isFinite(retrySeconds) ? retrySeconds : 15) * 1000, 120000));
        ttsUnavailableUntil = Date.now() + waitMs;
      }
    }
  }

  debug('tts_unavailable', { provider: 'remote_edge' });
  return null;
}

function nativePlaybackHandle(playbackId) {
  const stop = () => {
    try { void getNativeVoicePlugin()?.stopPlayback?.({ playbackId, reason: 'cancelled' }); } catch { /* noop */ }
  };
  // Existing UI code holds an HTMLAudio-like ref. These methods make that ref
  // safely cancellable in Android without exposing MediaPlayer to React.
  return { pause: stop, load: () => {}, removeAttribute: () => {}, stop };
}

async function playNativeAthenaAudio(base64Audio, { signal, onCreated, onStart, onDebug } = {}) {
  const plugin = getNativeVoicePlugin();
  if (!plugin) return null;

  const playbackId = `athena-tts-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const handle = nativePlaybackHandle(playbackId);
  let started = false;
  let aborted = Boolean(signal?.aborted);
  const debug = onDebug || appendAthenaVoiceDiagnostic;
  const removeStateListener = await addNativeVoiceListener('playbackState', (detail) => {
    if (detail?.playbackId !== playbackId) return;
    if (detail?.state === 'started') {
      if (aborted) {
        handle.stop();
        return;
      }
      started = true;
      debug('tts_playback_started', { provider: 'edge_francisca_neural', native: true });
      onStart?.();
    }
    if (detail?.state === 'ended') debug('tts_playback_finished', { provider: 'edge_francisca_neural', native: true });
    if (detail?.state === 'error') debug('tts_playback_error', { provider: 'edge_francisca_neural', native: true, reason: detail?.reason || 'native_player_error' });
  });
  const cancel = () => {
    aborted = true;
    handle.stop();
  };

  onCreated?.(handle);
  signal?.addEventListener?.('abort', cancel, { once: true });
  try {
    if (aborted) return { status: 'cancelled', started: false };
    const result = await plugin.playMp3({ playbackId, audioBase64: base64Audio, mimeType: 'audio/mpeg' });
    return {
      status: result?.status || (started ? 'finished' : 'error'),
      started: Boolean(result?.started ?? started),
    };
  } catch (error) {
    debug('tts_playback_error', { provider: 'edge_francisca_neural', native: true, reason: error?.message || 'native_player_failed' });
    return { status: aborted ? 'cancelled' : 'error', started };
  } finally {
    signal?.removeEventListener?.('abort', cancel);
    removeStateListener?.();
  }
}

/** Reproduz uma resposta e só marca "falando" após o áudio iniciar de fato. */
export function playAthenaAudio(base64Audio, { signal, onCreated, onStart, onDebug } = {}) {
  if (isAndroid && isNativeVoicePluginAvailable()) {
    return playNativeAthenaAudio(base64Audio, { signal, onCreated, onStart, onDebug });
  }

  return new Promise((resolve) => {
    const audio = new Audio(`data:audio/mpeg;base64,${base64Audio}`);
    let finished = false;
    let started = false;
    const finish = (status) => {
      if (finished) return;
      finished = true;
      signal?.removeEventListener?.('abort', cancel);
      resolve({ status, started });
    };
    const cancel = () => {
      try { audio.pause(); audio.removeAttribute('src'); audio.load(); } catch { /* noop */ }
      finish('cancelled');
    };

    onCreated?.(audio);
    audio.onplay = () => {
      started = true;
      (onDebug || appendAthenaVoiceDiagnostic)('tts_playback_started', { provider: 'remote_edge' });
      onStart?.();
    };
    audio.onended = () => finish('finished');
    audio.onerror = () => {
      (onDebug || appendAthenaVoiceDiagnostic)('tts_playback_error', { provider: 'remote_edge' });
      finish('error');
    };
    signal?.addEventListener?.('abort', cancel, { once: true });
    audio.play().catch(() => {
      (onDebug || appendAthenaVoiceDiagnostic)('tts_playback_blocked', { provider: 'remote_edge' });
      finish('blocked');
    });
  });
}
